import type { Application } from '@src/@types/app';
import { Experiments } from '@src/constants/abTest';
import { CHANNELS } from '@src/constants/advertising';
import { DEFAULT_PRELOADED_LIMIT, FILTERS_NAMES } from '@src/constants/filters';
import type { ListKeys } from '@src/constants/microcredits';
import { getUserPhoneForChecker } from '@src/helpers/getUserPhoneForChecker';
import { CookieKey } from '@src/modules/cookie';
import { fetchOffers } from '@src/reducers/microcredits';
import { isServer } from '@src/utils';
import { formatForSEAOffersLimitExperiment } from '@src/utils/formatForSEAOffersLimitExperiment';
import { getCookieFromServerOrClient } from '@src/utils/getCookieFromServerOrClient';

import { getPageListQuery } from './getPageListQuery';

interface IListParams {
  key: ListKeys;
  defaultFiltersMode: string;
  isSEAOffersLimitExperimentActive?: boolean;
  allowFullDealOffersExperiment?: boolean;
}

export const getPageListData = async (ctx: Application.ReduxNextPageContext, listParams: IListParams) => {
  const {
    store: { dispatch, getState },
  } = ctx;
  const {
    key,
    defaultFiltersMode,
    isSEAOffersLimitExperimentActive,
    allowFullDealOffersExperiment = true,
  } = listParams;

  const isFullDealOffersExperimentActive =
    allowFullDealOffersExperiment &&
    getState().metadata.abTestingInfo.experiments?.[Experiments.VITRIN_WITH_FULL_DEAL_OFFERS] === '1';

  const filterParams = getFilterParams(ctx, isFullDealOffersExperimentActive);

  const pageListQuery = getPageListQuery({ ctx, defaultFiltersMode, isFullDealOffersExperimentActive });

  const { amount } = pageListQuery;

  if (isSEAOffersLimitExperimentActive) {
    delete pageListQuery.amount;
  }

  await dispatch(
    fetchOffers({
      query: pageListQuery,
      params: filterParams,
      key,
      ...(isServer && { preloadedLimit: DEFAULT_PRELOADED_LIMIT }),
      ...(isSEAOffersLimitExperimentActive && {
        formatOffers: (offers) => formatForSEAOffersLimitExperiment(offers, amount),
        preloadedLimit: 16,
      }),
    }),
  );
};

function getFilterParams(ctx: Application.ReduxNextPageContext, isFullDealOffersExperimentActive: boolean) {
  const { hasUtmLabel } = ctx.store.getState().route;

  const clientId = getCookieFromServerOrClient(ctx, CookieKey._ym_uid);

  return {
    [FILTERS_NAMES.LOCATION]: ctx.params.locationRoute,
    [FILTERS_NAMES.METADATA]: true,
    [FILTERS_NAMES.ROUTE]: { asPath: ctx.asPath ?? '', query: ctx.query ?? {} },
    ...(!hasUtmLabel && {
      [FILTERS_NAMES.ADS_ONLY]: true,
      [FILTERS_NAMES.HAS_OFFERS_DUPLICATE]: true,
    }),
    ...(Boolean(clientId) && {
      [FILTERS_NAMES.CLIENT_ID]: clientId,
    }),
    ...getUserPhoneForChecker(ctx),

    ...(isFullDealOffersExperimentActive &&
      !hasUtmLabel && {
        [FILTERS_NAMES.CHANNEL]: CHANNELS.FULL_DEAL,
        [FILTERS_NAMES.ADS_ONLY]: true,
        [FILTERS_NAMES.HAS_OFFERS_DUPLICATE]: false,
      }),
  };
}
